import { BASE_URL, CONFIG } from 'common/config.js'
import { tokenService } from 'common/token-service.js'
import { reachCheckpoint } from 'hooks/usePerfStep.js'
import { setMeasurement } from 'src/common/sentry-client'
import emitter from '@/services/event-emitter.js'

let socket = null
let timer = null
let authRetryTimer = null
let wsIntervalReconnect = CONFIG.WS.WS_INTERVAL_RECONNECT
let authTokenOverride = null

const WS_AUTH_RETRY_DELAY = 250
const WS_AUTH_RETRY_COUNT = 20

function listenSocket(e) {
  const { text } = JSON.parse(e.data)
  const { message_type, content } = text

  emitter.emit(message_type, content)
}

let firstWsConnected = false

function handleOpen() {
  if (!firstWsConnected) {
    reachCheckpoint('app.ws_connect_time')
    firstWsConnected = true
  }
  setMeasurement('app.ws_connect_success_count', 1, 'none')
  clearAuthRetryTimer()
  sendAuthToken()

  keepConnectionAlive()
}

async function keepConnectionAlive() {
  try {
    while (socket && socket.readyState === WebSocket.OPEN) {
      await sendPing()
      await new Promise((resolve) => setTimeout(resolve, CONFIG.WS.PONG_INTERVAL))
    }
  } catch {
    console.error('Error or ping timeout, closing socket')
    if (socket) {
      socket.close()
    }
  }
}

function sendPing() {
  return new Promise((resolve, reject) => {
    const start = performance.now()
    sendMessage({ action: 'ping' })

    const timeout = setTimeout(() => {
      wsIntervalReconnect = 0
      reject(new Error('Pong timeout'))
    }, CONFIG.WS.PONG_TIMEOUT)

    const onMessage = (event) => {
      const { text } = JSON.parse(event.data)
      if (text.message_type === 'pong') {
        setMeasurement('app.ws_ping_latency', performance.now() - start, 'millisecond')

        clearTimeout(timeout)
        if (socket) {
          socket.removeEventListener('message', onMessage)
        }
        resolve()
      }
    }

    socket.addEventListener('message', onMessage)
  })
}

function reconnect() {
  socket = null
  clearTimeout(timer)
  clearAuthRetryTimer()

  timer = setTimeout(() => connectToWs(), wsIntervalReconnect)
}

export function connectToWs(options = {}) {
  updateConnectionOptions(options)

  if (socket && socket.readyState === WebSocket.OPEN) {
    sendAuthToken()
    return
  }

  if (socket && socket.readyState === WebSocket.CONNECTING) {
    scheduleAuthTokenUpdate()
    return
  }

  setMeasurement('app.ws_connect_attempt_count', 1, 'none')

  wsIntervalReconnect = CONFIG.WS.WS_INTERVAL_RECONNECT

  const urlWs = process.env.WS_DOMAIN || `wss://${location.hostname}${BASE_URL}/api/g/ws/`
  socket = new WebSocket(urlWs)
  scheduleAuthTokenUpdate()

  socket.onopen = handleOpen
  socket.onmessage = listenSocket
  socket.onclose = reconnect
  socket.onerror = () => {
    setMeasurement('app.ws_connect_failure_count', 1, 'none')
    reconnect()
  }
}

export function sendMessage(json) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(json))
    return true
  }
  return false
}

function updateConnectionOptions(options) {
  const normalizedOptions = typeof options === 'string' ? { token: options } : options || {}

  if (Object.prototype.hasOwnProperty.call(normalizedOptions, 'token')) {
    authTokenOverride = normalizedOptions.token || null
  }
}

function getAuthToken() {
  return authTokenOverride || tokenService.getToken()
}

function sendAuthToken() {
  const token = getAuthToken()

  if (!token || !socket || socket.readyState !== WebSocket.OPEN) {
    return false
  }

  sendMessage({
    action: 'update_token',
    content: token,
  })

  return true
}

function scheduleAuthTokenUpdate(attempt = 0) {
  if (!getAuthToken()) {
    return
  }

  clearAuthRetryTimer()

  authRetryTimer = setTimeout(() => {
    authRetryTimer = null

    if (sendAuthToken() || attempt >= WS_AUTH_RETRY_COUNT) {
      return
    }

    scheduleAuthTokenUpdate(attempt + 1)
  }, WS_AUTH_RETRY_DELAY)
}

function clearAuthRetryTimer() {
  if (authRetryTimer) {
    clearTimeout(authRetryTimer)
    authRetryTimer = null
  }
}
